import React from 'react';
import type { BrokerBoardCluster } from '../../api/types';
import { PRIORITY_ORDER, PRIORITY_HELP } from './brokerViewTokens';
import { SeverityBar } from './SeverityBar';
import './BoardClusterLens.css';

const fmtMoney = (n?: number | null) => (n == null ? '–' : `$${Math.round(n).toLocaleString()}`);
const fmtInt = (n?: number | null) => (n == null ? '–' : Math.round(n).toLocaleString());
const fmtNum = (n?: number | null, digits = 1) => (n == null ? '–' : n.toFixed(digits));

/** §13.5 render the fixed MMR band token ('45k-65k' | '<10k' | '130k+' | 'unknown')
 * as a human value range ('$45–65K'); null (no chip) for unknown/blank. */
const fmtBand = (band?: string | null): string | null => {
  if (!band || band === 'unknown') return null;
  const up = (s: string) => s.replace(/k$/i, 'K');
  if (band.startsWith('<')) return `< $${up(band.slice(1))}`;
  if (band.endsWith('+')) return `$${up(band.slice(0, -1))}+`;
  const [lo, hi] = band.split('-');
  if (lo && hi) return `$${lo.replace(/k$/i, '')}–${up(hi)}`;
  return `$${up(band)}`;
};

const SEVERITY: Record<number, { label: string; bg: string; fg: string }> = {
  0: { label: 'Critical', bg: '#fee2e2', fg: '#b91c1c' },
  1: { label: 'High', bg: '#fef3c7', fg: '#a16207' },
  2: { label: 'Medium', bg: '#dbeafe', fg: '#1d4ed8' },
  3: { label: 'Low', bg: '#dcfce7', fg: '#15803d' },
};

/**
 * The cluster slice card header: cohort identity, urgency, price range, and the
 * buyer reach (matched buyers + a compact top-buyer summary). The cluster's VINs
 * live in the shared `BrokerVinTable` drawer the `BrokerSliceView` shell renders
 * on expand (each VIN shows its best buyer + supports the Match-Type filter).
 *
 * `onBulkAdd` (§5) stages every VIN in the cluster to its single top buyer in one
 * click; `bulkAdding` shows the in-flight state.
 */
export const BrokerClusterCardHeader: React.FC<{
  cluster: BrokerBoardCluster;
  onBulkAdd?: (cluster: BrokerBoardCluster) => void;
  bulkAdding?: boolean;
}> = ({ cluster, onBulkAdd, bulkAdding }) => {
  const sev = SEVERITY[cluster.severity_tier] ?? SEVERITY[3];
  const title = [cluster.make, cluster.model].filter(Boolean).join(' ') || 'Unknown';
  const cohortBits = [
    cluster.cohort_year,
    cluster.cohort_mileage ? `${cluster.cohort_mileage} mi` : null,
    cluster.cohort_condition ? `CR ${cluster.cohort_condition}` : null,
    cluster.trim,
    cluster.drivetrain,
  ].filter(Boolean).join(' · ');
  const priceRange =
    cluster.min_price != null && cluster.max_price != null
      ? `${fmtMoney(cluster.min_price)}–${fmtMoney(cluster.max_price)}`
      : fmtMoney(cluster.avg_price);
  const bandLabel = fmtBand(cluster.mmr_band);
  const topBuyerNames = (cluster.top_buyers ?? [])
    .slice(0, 3)
    .map((b) => b.buyer_name || `Buyer ${b.buyer_uid}`)
    .join(', ');
  const pc = cluster.priority_counts;
  const pcTotal = pc ? pc.severity_critical + pc.severity_high + pc.severity_medium + pc.severity_low : 0;

  return (
    <>
      <div className="bcl-row-main">
        <span className="bcl-count" title="Similar VINs (same buyer-match signature) you can market together">{fmtInt(cluster.vin_count)} VINs</span>
        <span className="bcl-sev" style={{ background: sev.bg, color: sev.fg }} title={PRIORITY_HELP[PRIORITY_ORDER[cluster.severity_tier]] ?? 'Urgency of the most-severe VIN in this cohort'}>{sev.label}</span>
        <span className="bcl-title">{title}</span>
        {cohortBits && <span className="bcl-cohort" title="Vehicle cohort (year / mileage / CR band / trim / drivetrain)">{cohortBits}</span>}
        {bandLabel && (
          <span
            className="bcl-band"
            title="Market-value (MMR) band — clusters are split into fixed value tiers so a group's units stay price-comparable. This is the cluster's value scope, based on MMR (distinct from the asking-price range below)."
          >{bandLabel}</span>
        )}
        {cluster.matched_buyers > 0 && (
          <span className="bcl-buyers-static" title={topBuyerNames ? `Top buyers: ${topBuyerNames}` : 'Buyers this cluster reaches (top-5 for one or more of its VINs)'}>
            {fmtInt(cluster.matched_buyers)} buyers
          </span>
        )}
        {onBulkAdd && cluster.top_buyer_uid && (
          <button
            type="button"
            className="bcl-bulk-add"
            disabled={bulkAdding}
            onClick={(e) => { e.stopPropagation(); onBulkAdd(cluster); }}
            title={`Add all ${fmtInt(cluster.vin_count)} VINs to ${cluster.top_buyer_name || `Buyer ${cluster.top_buyer_uid}`} (the cluster's top buyer)`}
          >
            {bulkAdding ? 'Adding…' : `+ ${fmtInt(cluster.vin_count)} to top buyer`}
          </button>
        )}
      </div>
      <div className="bcl-row-stats">
        <span title="Price range across the cohort"><b>{priceRange}</b></span>
        <span><b>{cluster.avg_year ?? '–'}</b> avg yr</span>
        <span><b>{fmtInt(cluster.avg_mileage)}</b> mi</span>
        <span><b>{fmtNum(cluster.avg_cr_grade)}</b> CR</span>
        {topBuyerNames && (
          <span className="bcl-buyers-summary" title={`Top buyers: ${topBuyerNames}`}>· {topBuyerNames}</span>
        )}
      </div>
      {pc && <div className="bv-slice-sev"><SeverityBar counts={pc} total={pcTotal} /></div>}
    </>
  );
};
